import { useEffect, useMemo } from 'preact/hooks';
import { formatarIndicador, posicaoTexto } from '../format';
import { useApi } from '../hooks';
import { criarEscalaIndicador } from '../mapa/escalas';
import { eixoSel, indicadorSel } from '../state';
import { tema } from '../tema';
import type { EixoInfo, IndicadorPanorama, MapaIndicador, MapaResposta } from '../types';

/**
 * Tudo da camada "Indicadores oficiais" do mapa: eixos, indicadores do eixo escolhido, o mapa do indicador e a escala.
 * Só consulta a API quando a camada está ativa (`ativa`), para não gastar requisições à toa.
 */
export function useCamadaIndicador(ativa: boolean) {
  const eixos = useApi<EixoInfo[]>(ativa ? '/indicadores/eixos' : null);
  const eixo = eixoSel.value;
  const lista = useApi<IndicadorPanorama[]>(ativa ? `/indicadores?eixo=${eixo}` : null);
  // Só indicadores com dados: um que espera planilha ainda não tem o que mostrar no mapa.
  const doEixo = (lista.dados ?? []).filter((i) => i.eixo === eixo && i.periodo !== null);

  // Se o indicador escolhido não é deste eixo (trocou de eixo), cai no primeiro do eixo.
  const slug = doEixo.find((i) => i.slug === indicadorSel.value)?.slug ?? doEixo[0]?.slug ?? '';
  useEffect(() => {
    if (slug && slug !== indicadorSel.value) indicadorSel.value = slug;
  }, [slug]);

  const mapa = useApi<MapaIndicador>(ativa && slug ? `/indicadores/${slug}/mapa` : null);
  const m = mapa.dados && mapa.dados.indicador.slug === slug ? mapa.dados : null;
  const info = m?.indicador ?? null;

  const temaAtual = tema.value;
  const escala = useMemo(
    () => (m && info ? criarEscalaIndicador(Object.values(m.municipios).map((x) => x.valor), info.sentido, (v) => formatarIndicador(v, info.unidade, info.casas), info.nome, temaAtual) : null),
    [m, temaAtual],
  );

  // Mesmo formato das outras camadas, para o mapa desenhar sem saber a diferença.
  const dadosMapa: MapaResposta | null = m && {
    camada: 'indicadores',
    data: m.periodo ?? '',
    unidade: m.indicador.unidade,
    municipios: Object.fromEntries(Object.entries(m.municipios).map(([cod, e]) => [cod, { valor: e.valor, total: 0, atrasadas: 0 }])),
    escala: m.escala,
    comDados: m.comDados,
    semDados: m.semDados,
  };

  const textoDica = (cod: string): string[] => {
    if (!m || !info) return [];
    const e = m.municipios[cod];
    if (!e) return ['Sem dado nesta fonte'];
    const linhas = [formatarIndicador(e.valor, info.unidade, info.casas), `${posicaoTexto(e.posicao, e.de)}${m.periodo ? ` · ${m.periodo}` : ''}`];
    if (e.percentil !== null) linhas.push(`Melhor que ${Math.round(e.percentil)}% dos municípios`);
    return linhas;
  };

  return {
    eixos: eixos.dados ?? [],
    indicadores: doEixo,
    slug,
    info,
    mapa: m,
    escala,
    dadosMapa,
    textoDica,
    carregando: mapa.carregando || lista.carregando,
    erro: mapa.erro ?? lista.erro,
  };
}

export type CamadaIndicador = ReturnType<typeof useCamadaIndicador>;
